<template>
  <form
    class="rounded-md border shadow-md"
    :class="focused && 'border-primary'"
    data-cy="log-quick-submit"
    @submit.prevent="onSubmit"
  >
    <slot name="tabs" />
    <textarea
      v-if="!autoResizing"
      ref="textareaRef"
      v-model="text"
      class="w-full flex-1 resize-none border-gray-200 text-sm text-gray-600 placeholder-gray-400"
      :class="isModal ? 'h-[calc(100%-82px)]' : 'rounded-sm'"
      :placeholder="placeholder"
      :rows="isSms ? 1 : 2"
      :maxlength="maxLength"
      @focus="focused = true"
      @blur="focused = false"
      @keydown.meta.enter="onSubmit"
      @keydown.ctrl.enter="onSubmit"
      @keydown.exact="voicePromptRef?.stopRecording()"
      @keydown.meta="voicePromptRef?.stopRecording()"
      @keydown.ctrl="voicePromptRef?.stopRecording()"
    />
    <autoresizing-text-area
      v-else
      ref="textareaRef"
      v-model="text"
      class="autoresizing-text-area max-h-100 w-full overflow-y-auto rounded-md border-0 border-transparent text-sm text-gray-700 focus-within:outline-hidden focus:border-transparent focus:ring-0 focus:outline-hidden"
      :class="[
        isModal ? 'h-[calc(100%-82px)]' : 'rounded-sm',
        isSms ? '-mb-4 pr-8' : '-mb-7 pt-9',
      ]"
      :placeholder="placeholder"
      :rows="isSms ? 1 : 2"
      :auto-focus="focusOnMount"
      :maxlength="maxLength"
      @focus="focused = true"
      @blur="focused = false"
      @keydown.meta.enter="onSubmit"
      @keydown.ctrl.enter="onSubmit"
      @keydown.exact="voicePromptRef?.stopRecording()"
      @keydown.meta="voicePromptRef?.stopRecording()"
      @keydown.ctrl="voicePromptRef?.stopRecording()"
    />
    <slot name="supplemental" />
    <div
      v-if="isSms"
      class="absolute top-1 right-1 flex items-center space-x-1"
    >
      <voice-prompt
        ref="voicePromptRef"
        class="flex items-center justify-center"
        @text:updated="updateTextOnVoiceInput"
        @recording:finished="resetVoiceState"
      />
      <button
        type="submit"
        :disabled="isDisabled"
        data-cy="log-quick-submit-button"
        class="flex h-7 w-7 items-center justify-center rounded-sm"
        :class="
          isDisabled
            ? 'cursor-not-allowed bg-gray-300'
            : 'cursor-pointer bg-primary/10'
        "
      >
        <loader :is-loading="loading" />
        <icon-base
          :icon="IconSend"
          width="12"
          height="12"
          view-box="0 0 16 16"
          :class="isDisabled ? 'text-white' : 'text-primary'"
        />
      </button>
    </div>
    <div
      v-if="!isSms"
      class="relative flex items-center justify-between rounded-b-md bg-grey-hover"
      :class="[$slots.supplemental ? 'px-3 pt-1 pb-3' : 'p-3']"
    >
      <slot name="actionBar"></slot>

      <div
        class="action-button-wrapper flex w-7/10 items-center justify-end space-x-3"
      >
        <voice-prompt
          ref="voicePromptRef"
          class="flex items-center justify-center"
          @text:updated="updateTextOnVoiceInput"
          @recording:finished="resetVoiceState"
        />
        <slot name="buttons">
          <div class="space-x-1">
            <span class="font-bold">{{ $t("COMMON.RETURN") }}</span>
            <span v-if="returnPlaceholder">
              {{ returnPlaceholder }}
            </span>
          </div>
          <button
            type="submit"
            :disabled="isDisabled"
            data-cy="log-quick-submit-button"
            class="flex h-8 w-8 items-center justify-center rounded-sm"
            :class="
              isDisabled
                ? 'cursor-not-allowed bg-gray-300'
                : 'cursor-pointer bg-primary'
            "
          >
            <loader :is-loading="loading" />
            <icon-base :icon="IconSend" class="text-white" />
          </button>
        </slot>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from "vue";

import debounce from "lodash/debounce";

import { AnalyticsEvent } from "@/enums/analytics";
import { DEBOUNCE_BUFFER, TEXTAREA_DEBOUNCE } from "@/helpers/constants";
import { useVoiceTextInput } from "@/hooks/useVoiceTextInput";
import { useAnalyticsStore } from "@/stores/analytics";

import VoicePrompt from "@/components/VoicePrompt.vue";
import IconSend from "@/components/icons/IconSend.vue";
import AutoresizingTextArea from "@/views/deals/components/dealNotes/AutoresizingTextArea.vue";

const {
  modelValue,
  loading,
  placeholder = "",
  returnPlaceholder = "",
  textRequired = true,
  focusOnMount = false,
  isModal = false,
  isSms = false,
  autoResizing = false,
  maxLength,
} = defineProps<{
  modelValue: string;
  loading?: boolean;
  placeholder?: string;
  returnPlaceholder?: string;
  textRequired?: boolean;
  focusOnMount?: boolean;
  isModal?: boolean;
  isSms?: boolean;
  autoResizing?: boolean;
  maxLength?: number;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: string];
  submit: [];
}>();

const { trackEvent } = useAnalyticsStore();

const focused = ref(false);
const textareaRef = useTemplateRef("textareaRef");
const voicePromptRef = useTemplateRef("voicePromptRef");

const text = computed({
  get: () => modelValue,
  set: (value) => emit("update:modelValue", value),
});

const { updateText: updateTextOnVoiceInput, resetState: resetVoiceRefs } =
  useVoiceTextInput(text);

const isDisabled = computed(
  () => (textRequired && !text.value.trim()) || Boolean(loading)
);

const resetVoiceState = () => {
  const textLength = resetVoiceRefs();
  if (textLength > 0) {
    trackEvent(AnalyticsEvent.LogVoiceInputUsed, {
      text_length: textLength,
    });
  }
};

const onSubmit = debounce(
  () => {
    if (isDisabled.value) {
      return;
    }
    voicePromptRef.value?.stopRecording();
    emit("submit");
  },
  TEXTAREA_DEBOUNCE + DEBOUNCE_BUFFER,
  { leading: false, trailing: true }
);

onMounted(() => {
  if (textareaRef.value && focusOnMount) {
    textareaRef.value.focus();
  }
});

defineExpose({
  textareaRef,
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

textarea {
  border: none;
  outline: none;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
}
</style>
